import React, { memo, useMemo, ChangeEvent } from 'react';

import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';

import useLocales from 'src/hooks/useLocales';

import { CountryProps } from '../types/elementSettingsTypes';
import { COUNTRY_CODES } from '../constants/elementSettingsConstants';
import { CalendarEventType } from '../../../../../@types/calendar/types';

type ElementDateRangeSettingsProps = {
  daysOfCountry: CountryProps;
  dateRangeTemplate: string | undefined;
  openedTabIndex: number;
  eventTypesList: CalendarEventType[];
  calendarEventType: CalendarEventType | null;
  onDaysOfCountryChange: (country: CountryProps) => void;
  onDateRangeTemplateChange: (template: string) => void;
  onTabIndexChange: (index: number) => void;
  onCalendarEventTypeChange: (eventType: CalendarEventType | null) => void;
  currentLang: any;
};

const ElementDateRangeSettings: React.FC<ElementDateRangeSettingsProps> = ({
  daysOfCountry,
  dateRangeTemplate,
  openedTabIndex,
  eventTypesList,
  calendarEventType,
  onDaysOfCountryChange,
  onDateRangeTemplateChange,
  onTabIndexChange,
  onCalendarEventTypeChange,
  currentLang,
}) => {
  const { t } = useLocales();

  const countries = useMemo(
    () => [
      {
        code: COUNTRY_CODES.LT,
        label: t('shared.countries.lt'),
      },
      {
        code: COUNTRY_CODES.LV,
        label: t('shared.countries.lv'),
      },
      {
        code: COUNTRY_CODES.EE,
        label: t('shared.countries.ee'),
      },
    ],
    [t]
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {eventTypesList.length > 0 && (
        <Autocomplete
          id="km9ol08b"
          value={calendarEventType}
          options={eventTypesList}
          getOptionLabel={(option) => {
            const itemName = JSON.parse(option.name);
            return itemName[currentLang.value];
          }}
          onChange={(event, value) => onCalendarEventTypeChange(value)}
          renderInput={(params) => (
            <TextField {...params} label={t('calendar.events.typesCalendarEvents')} />
          )}
          size="small"
        />
      )}
      <Autocomplete
        value={daysOfCountry}
        onChange={(event: any, newValue: CountryProps | null) =>
          onDaysOfCountryChange(newValue || countries[0])
        }
        size="small"
        fullWidth
        disableClearable
        options={countries}
        autoHighlight
        getOptionLabel={(option) => option.label}
        renderOption={(props, option) => (
          <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
            <img
              loading="lazy"
              width="20"
              src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
              srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
              alt={option.label}
            />
            {option.label} ({option.code})
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('forms.elements.settings.daysOffByCountry')}
            slotProps={{
              ...params.slotProps,

              htmlInput: {
                ...params.slotProps.htmlInput,
              }
            }}
          />
        )}
      />
      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={openedTabIndex}>
            <Tab
              label={t('forms.elements.settings.variables')}
              onClick={() => onTabIndexChange(0)}
              id="GAqf57F"
            />
            <Tab
              label={t('forms.elements.settings.example')}
              onClick={() => onTabIndexChange(1)}
              id="TWEc3vb"
            />
          </Tabs>
        </Box>

        <Box
          sx={[
            {
              mt: 2,
            },
            openedTabIndex === 0
              ? {
                  display: 'block',
                }
              : {
                  display: 'none',
                },
          ]}
        >
          <Alert severity="warning" icon={false} sx={{ position: 'relative' }}>
            <strong>[s]</strong> - {t('forms.elements.settings.startDate')};
            <br />
            <strong>[e]</strong> - {t('forms.elements.settings.endDate')};
            <br />
            <strong>[c]</strong> - {t('forms.elements.settings.countedCalendarDays')};
            <br />
            <strong>[w]</strong> - {t('forms.elements.settings.countedWorkingDays')}.
          </Alert>
        </Box>
        <Box
          sx={[
            {
              mt: 2,
            },
            openedTabIndex === 1
              ? {
                  display: 'block',
                }
              : {
                  display: 'none',
                },
          ]}
        >
          <Alert severity="info" icon={false} sx={{ position: 'relative' }}>
            <strong>{t('forms.elements.settings.template')}</strong> - from [s] to [e] ([w] working
            days);
            <br />
            <strong>{t('forms.elements.settings.userFilled')}</strong> - 08/20/2023-08/26/2023;
            <br />
            <strong>{t('forms.elements.settings.inTheGeneratedDocument')}</strong> - from 8/20/2023 to
            8/26/2023 (5 working days);
            <br />
          </Alert>
        </Box>
      </Box>
      <TextField
        value={dateRangeTemplate}
        size="small"
        label={t('forms.elements.settings.template')}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          onDateRangeTemplateChange(event.target.value);
        }}
        id="POImh7u"
      />
    </Box>
  );
};

export default memo(ElementDateRangeSettings);
